import { useEffect, useState } from "react"
import { toast } from "react-toastify"
import classNames from "classnames"

import { useLazyGetWHDevicesQuery } from "store/api/warehouseManagerApi"
import { useAppDispatch, useAppSelector } from "store/hooks"
import { setSelectedDeviceIDs } from "store/tempDataSlise"
import { warehouseManagerDeviceStatusOptions } from "constants/SelectFieldOptions"
import { UITypes } from "types"

import { convertWHManagerDevicessToTableRows } from "utils/convertData"
import getErrorMessage from "utils/getErrorMessage"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { AddDeviceModal } from "modals/AddDeviceModal"
import { DamageDeviceModalForWarehouse } from "modals/DamageDeviceModalForWarehouse"
import { MissingModalWarehouse } from "modals/MissingModalWarehouse"
import { ReturnFromMissingModalWarehouse } from "modals/ReturnFromMissingModalWarehouse"
import { ContentWrapper } from "components/common/ContentWrapper"
import { Pagination } from "components/common/Pagination"
import { ButtonNew } from "components/form/ButtonNew"
import { StatusOfPhysical } from "components/table/columnComponents/StatusOfPhysical"
import TableWrapper from "components/table/TableWrapper"
import { WidgetDevicesStatus } from "components/widgets/WidgetDevicesStatus"

import styles from "./index.module.scss"

// TABLE HEADER
const tableHeaderCells: UITypes.TableHeaderCell[] = [
  {
    title: "",
    name: "checkbox",
    type: "checkbox",
    size: "mini",
    cellComponent: "checkbox",
  },
  {
    title: "S/N",
    type: "sort-search",
    size: "large",
    name: "sn",
  },
  {
    title: "Date of Input",
    type: "sort-date",
    size: "large",
    name: "input_date",
  },
  {
    title: "Status",
    type: "select",
    size: "large",
    name: "phisical_status",
    options: warehouseManagerDeviceStatusOptions,
    cellComponent: StatusOfPhysical,
  },
]

export const Devices = () => {
  const [triggerGetWHDevices, { data, isFetching }] = useLazyGetWHDevicesQuery()
  const dispatch = useAppDispatch()
  const { urlQueryParams } = useUrlQueryParams()
  const selectedDeviceIDs = useAppSelector(state => state.tempData.selectedDeviceIDs)
  const [isHeaderChecked, setHeaderChecked] = useState(false)
  const [isFirstRender, setFirstRender] = useState(true)
  const [showMissingModal, setShowMissingModal] = useState(false)
  const [showReturnFromMissingModal, setShowReturnFromMissingModal] = useState(false)
  const [showDamageModal, setShowDamageModal] = useState(false)
  const [showAddDeviceModal, setShowAddDeviceModal] = useState(false)
  const [updateIndex, setUpdateIndex] = useState(0)

  const toggleAllItems = () => {
    if (isHeaderChecked) {
      dispatch(setSelectedDeviceIDs([]))
      setHeaderChecked(false)
    } else {
      const result = data?.data?.map(item => item?.id)
      if (result) {
        dispatch(setSelectedDeviceIDs(result))
      }
      setHeaderChecked(true)
    }
  }

  const onSelectItem = (id?: number) => {
    if (selectedDeviceIDs?.some(item => item === id)) {
      dispatch(setSelectedDeviceIDs(selectedDeviceIDs?.filter(item => item !== id)))
    } else {
      dispatch(setSelectedDeviceIDs([...selectedDeviceIDs, ...[id]] as number[]))
    }
  }

  const onDamagedClick = () => {
    setShowDamageModal(true)
  }

  const hasAnyMissingDevice = () => {
    if (!selectedDeviceIDs?.length || !data?.data) return false
    return data.data
      .filter(device => selectedDeviceIDs.includes(device.id))
      .some(device => device.phisical_status === "missing")
  }

  const allDevicesAreMissing = () => {
    if (!selectedDeviceIDs?.length || !data?.data) return false
    return data.data
      .filter(device => selectedDeviceIDs.includes(device.id))
      .every(device => device.phisical_status === "missing")
  }

  const onMissingClick = () => {
    if (hasAnyMissingDevice() && !allDevicesAreMissing()) {
      return
    }

    if (hasAnyMissingDevice()) {
      setShowReturnFromMissingModal(true)
    } else {
      setShowMissingModal(true)
    }
  }

  const fetchData = async () => {
    sendRequest(urlQueryParams)
  }

  const sendRequest = async (params: UITypes.Params) => {
    try {
      const res = await triggerGetWHDevices(params)
      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
      }
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    if (isFirstRender) {
      setFirstRender(false)
      return
    }
    fetchData()
  }, [urlQueryParams, isFirstRender])

  const handleSuccess = () => {
    setShowMissingModal(false)
    setShowReturnFromMissingModal(false)
    setShowDamageModal(false)
    setShowAddDeviceModal(false)
    setUpdateIndex(prev => prev + 1)
    dispatch(setSelectedDeviceIDs([]))
    fetchData()
  }

  return (
    <>
      <WidgetDevicesStatus updateIndex={updateIndex} />
      <ContentWrapper title="Devices">
        <nav className={styles.tableNavWrapper}>
          <div>
            <ButtonNew onClick={() => setShowAddDeviceModal(true)} color="blue">
              Add New Thermostat
            </ButtonNew>
          </div>
          <div className={styles.tableNavButtonsWrapper}>
            {selectedDeviceIDs?.length ? (
              <>
                <span
                  onClick={onMissingClick}
                  className={classNames(styles.tagButton, styles.yellow, {
                    [styles.disabled]: hasAnyMissingDevice() && !allDevicesAreMissing(),
                  })}
                >
                  {hasAnyMissingDevice() ? "RETURN FROM MISSING" : "MARK AS MISSING"}
                </span>
                <span onClick={onDamagedClick} className={classNames(styles.tagButton, styles.red)}>
                  MARK AS DAMAGED
                </span>
              </>
            ) : (
              <>
                <span className={classNames(styles.tagButton)}>MARK AS MISSING</span>
                <span className={classNames(styles.tagButton)}>MARK AS DAMAGED</span>
              </>
            )}
          </div>
        </nav>
        <TableWrapper
          headerCells={tableHeaderCells}
          data={convertWHManagerDevicessToTableRows(data?.data)}
          isLoading={isFetching}
          onCheckboxChange={onSelectItem}
          onHeaderCheckboxChange={toggleAllItems}
          currentItemIDs={selectedDeviceIDs}
          isHeaderChecked={isHeaderChecked}
        />
        <Pagination meta={data?.meta} />
      </ContentWrapper>
      {showMissingModal && (
        <MissingModalWarehouse onCancel={() => setShowMissingModal(false)} onSuccess={handleSuccess} />
      )}
      {showReturnFromMissingModal && (
        <ReturnFromMissingModalWarehouse
          onCancel={() => setShowReturnFromMissingModal(false)}
          onSuccess={handleSuccess}
        />
      )}
      {showDamageModal && (
        <DamageDeviceModalForWarehouse onCancel={() => setShowDamageModal(false)} onSuccess={handleSuccess} />
      )}
      {showAddDeviceModal && <AddDeviceModal onCancel={() => setShowAddDeviceModal(false)} onSuccess={handleSuccess} />}
    </>
  )
}
